import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import useAnchoredPosition from "./useAnchoredPosition";

// Custom replacement for the toolbar's chip <select>s.
//
// The native control renders its option list with the OS, which sizes itself to
// the longest option — case-type names here run to a full sentence, so the list
// spilled across the whole window. This keeps the popup in a portal with a
// fixed, viewport-clamped box: the trigger stays chip-width with a truncated
// label, and long options wrap/ellipsize inside a scrollable panel instead of
// widening anything.

const MIN_PANEL_W = 200;
const MAX_PANEL_W = 340;
const MAX_PANEL_H = 320;

const normalize = (opt) =>
  typeof opt === "string" || typeof opt === "number"
    ? { value: String(opt), label: String(opt) }
    : opt;

export default function ChipSelect({
  value,
  onChange,
  options = [],
  // Shown on the trigger when nothing is picked; also the label of the
  // "clear this filter" first row.
  placeholder = "Select",
  placeholderValue = "all",
  ariaLabel,
  className = "",
  // Auto-enables once the list is long enough to be worth filtering.
  searchable,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const pos = useAnchoredPosition(open, triggerRef, {
    minWidth: MIN_PANEL_W,
    maxWidth: MAX_PANEL_W,
    maxHeight: MAX_PANEL_H,
  });

  const items = useMemo(() => {
    const rest = options.map(normalize).filter(Boolean);
    return [{ value: placeholderValue, label: placeholder }, ...rest];
  }, [options, placeholder, placeholderValue]);

  const canSearch = searchable ?? items.length > 8;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((o) => o.label.toLowerCase().includes(q));
  }, [items, query]);

  const selected = items.find((o) => o.value === value);
  const isPlaceholder = !selected || selected.value === placeholderValue;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  // Reset happens here rather than in an effect on `open` so it can't re-run
  // mid-typing and clobber the query.
  const openMenu = () => {
    setQuery("");
    const idx = items.findIndex((o) => o.value === value);
    setActiveIdx(idx >= 0 ? idx : 0);
    setOpen(true);
    if (canSearch) requestAnimationFrame(() => searchRef.current?.focus());
  };

  const commit = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = visible[activeIdx];
      if (opt) commit(opt);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        title={isPlaceholder ? undefined : selected.label}
        className={`inline-flex max-w-[150px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400/40 disabled:cursor-not-allowed disabled:opacity-60 ${
          isPlaceholder
            ? "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            : "border-yellow-400 bg-yellow-50 text-slate-800"
        } ${open ? "border-yellow-400 ring-2 ring-yellow-400/40" : ""} ${className}`}
      >
        <span className="truncate">{isPlaceholder ? placeholder : selected.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="listbox"
              aria-label={ariaLabel}
              style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}
              className="fixed z-[10000] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl"
              onKeyDown={onKeyDown}
            >
              {canSearch && (
                <div className="relative shrink-0 border-b border-slate-100 p-2">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActiveIdx(0);
                    }}
                    placeholder="Search..."
                    className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400/40"
                  />
                </div>
              )}

              <div
                className="overflow-y-auto overscroll-contain py-1"
                style={{ maxHeight: pos.maxHeight - (canSearch ? 48 : 0) }}
              >
                {visible.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-slate-400">No matches</p>
                ) : (
                  visible.map((opt, i) => {
                    const isSel = opt.value === value;
                    return (
                      <button
                        key={`${opt.value}-${i}`}
                        type="button"
                        role="option"
                        aria-selected={isSel}
                        title={opt.label}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => commit(opt)}
                        className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs leading-snug transition-colors ${
                          i === activeIdx ? "bg-yellow-50" : ""
                        } ${isSel ? "font-semibold text-slate-900" : "text-slate-700"}`}
                      >
                        <Check
                          className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-600 ${isSel ? "" : "invisible"}`}
                        />
                        {/* Long case-type names stay inside the panel: wrap to at
                            most 3 lines, then ellipsize. Full text is on title. */}
                        <span className="line-clamp-3 break-words">{opt.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
